import React from 'react';

interface EvaluationFiltersProps {
  // Status counts for options labels
  totalSubmissions: number;
  notStarted: number;
  drafts: number;
  completed: number;

  // Search
  searchQuery: string;
  onSearchChange: (value: string) => void;

  // Status filter
  filterStatus: string;
  onFilterStatusChange: (value: string) => void;

  // Category filter
  filterCategory: string;
  onFilterCategoryChange: (value: string) => void;
  allFilterCategories: string[];
  categoryCounts: Record<string, number>;
  userCategory: string;
  submissionsCount: number;

  // Results count
  filteredCount: number;

  // Clear all
  onClearFilters: () => void;
}

// Filters Section - Following SMDDJudgmentPage pattern
const EvaluationFilters: React.FC<EvaluationFiltersProps> = ({
  totalSubmissions,
  notStarted,
  drafts,
  completed,
  searchQuery,
  onSearchChange,
  filterStatus,
  onFilterStatusChange,
  filterCategory,
  onFilterCategoryChange,
  allFilterCategories,
  categoryCounts,
  userCategory,
  submissionsCount,
  filteredCount,
  onClearFilters,
}) => {
  const hasActiveFilters =
    filterStatus !== 'all' || filterCategory !== 'all' || searchQuery !== '';

  return (
    <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
      {/* Quick Status Info */}
      {totalSubmissions > 0 && (
        <div className="mb-4 rounded-lg p-3 bg-blue-50 border border-blue-200">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm font-baijamjuree font-medium text-blue-800">
              {notStarted > 0
                ? 'Ready to start evaluating submissions'
                : drafts > 0
                  ? 'Continue with your draft evaluations'
                  : 'All evaluations completed - great work!'}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 items-center">
        {/* Search Input */}
        <div className="flex-1">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search submissions..."
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 bg-white font-baijamjuree text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        {/* Status Filter */}
        <select
          value={filterStatus}
          onChange={e => onFilterStatusChange(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-baijamjuree text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
        >
          <option value="all">All Status ({totalSubmissions})</option>
          <option value="not-started">Not Started ({notStarted})</option>
          <option value="draft">Drafts ({drafts})</option>
          <option value="completed">Completed ({completed})</option>
        </select>

        {/* Category Filter - Show if judge has multiple categories OR if submissions have multiple categories */}
        {(allFilterCategories.length > 1 || userCategory.includes(',')) && (
          <select
            value={filterCategory}
            onChange={e => onFilterCategoryChange(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-baijamjuree text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          >
            <option value="all">All Categories ({submissionsCount})</option>
            {allFilterCategories.map(category => (
              <option key={category} value={category}>
                {category} ({categoryCounts[category] || 0})
              </option>
            ))}
          </select>
        )}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-4 py-2.5 font-baijamjuree font-medium text-sm transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Results Count */}
      <div className="mt-3 text-sm text-gray-600 font-baijamjuree">
        Showing {filteredCount} of {submissionsCount} submissions
        {filteredCount !== submissionsCount && (
          <span className="ml-2 text-blue-600">(filtered)</span>
        )}
      </div>
    </div>
  );
};

export default EvaluationFilters;
